import { QueryClient } from "@tanstack/react-query";

/**
 * Client-side singleton so every consumer (SRP island, job-detail island, main app tree, ...)
 * shares one QueryClient and its cache, mirroring `clientStoreSingleton` in
 * `redux/createStore.ts`. That singleton exists because an earlier version gave each island its
 * own throwaway Redux store, so a dispatch from one island never reached state the other could
 * see — the same failure mode applies here: a mutation from the job-detail island must invalidate
 * / update data the SRP island's `useQuery` calls are reading, which only works if they share one
 * QueryClient instance and therefore one cache.
 */
let clientQueryClientSingleton: QueryClient | null = null;

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Most of what this layer serves (session identity, user-state) is per-user data fetched
        // at most once per page load and then kept fresh via mutations' own cache updates, not by
        // background polling — so avoid refetch-on-focus churn by default. Individual queries
        // (e.g. useUser's staleTime: Infinity) can still override this.
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Returns the shared QueryClient. On the server this always returns a fresh instance (there is
 * no singleton across requests — each SSR render must not leak cached data between users, the
 * same reasoning `createStore`'s `isServer` branch applies to the Redux store). On the client it
 * lazily creates and then reuses one instance for the lifetime of the page.
 */
export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    return createQueryClient();
  }
  if (!clientQueryClientSingleton) {
    clientQueryClientSingleton = createQueryClient();
  }
  return clientQueryClientSingleton;
}

/** Reset the client QueryClient singleton (for tests only). */
export function resetQueryClientSingletonForTests(): void {
  clientQueryClientSingleton = null;
}
